/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, useContext, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";
import localforage from "localforage";
import Loader from "examples/Loader";
import useRequest from "hook/useRequest";
import { ReqMethods } from "enums";
import { loadConfig } from "config";
import env from "config/env";

const { GET } = ReqMethods;

// Material Dashboard 2 React main context
const UserContext = createContext();

// Setting custom name for the context which is visible on react dev tools
UserContext.displayName = "UserContext";

// Any mutation elsewhere that changes the logged-in user's own record
// should call queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY })
// to refresh every consumer of useUserInfo().
const USER_QUERY_KEY = ["user"];

// Material Dashboard 2 React context provider
function UserProvider({ children }) {
  const request = useRequest();
  const queryClient = useQueryClient();
  const pathname = window.location.pathname;
  const encryptedData = pathname.split("/")[1];

  const { data, isLoading } = useQuery({
    // encryptedData identifies which email:locationId this session is for -
    // routes with no encrypted segment (/404, /maybe_connection/:agency_id,
    // /integrations/*) aren't part of this bootstrap flow, same as before.
    queryKey: [...USER_QUERY_KEY, encryptedData],
    queryFn: async () => {
      // /integrations/* isn't part of this bootstrap flow (see comment
      // above) but its first path segment ("integrations") happens to be
      // valid base64, so atob() below wouldn't throw and this would fall
      // through to a GET /user call with garbage email/locationId.
      if (pathname.startsWith("/integrations/")) {
        return { user: {}, userEmail: undefined };
      }

      let decryptedData;
      try {
        decryptedData = window.atob(encryptedData);
      } catch (error) {
        return { user: {}, userEmail: undefined };
      }
      const [email, locationId] = decryptedData.split(":");

      const token = await localforage.getItem("token");
      const { response } = await request(GET, "/user", {
        query: `email=${email}&locationId=${locationId}`,
        token,
      });
      if (!response) return { user: {}, userEmail: undefined };

      const { token: freshToken, ...user } = response.data;
      await localforage.setItem("token", freshToken);
      await localforage.setItem("user", user);
      response.agency && localforage.setItem("agency", response.agency);

      // Refreshes the shared `env` with the real userId and the fresh
      // token this login just minted - the boot-time loadConfig() call in
      // bootstrap() fires before this, with neither, so it used the "user"
      // placeholder and no auth header.
      loadConfig(freshToken).then((config) => Object.assign(env, config));

      return { user: { ...response.data, token: freshToken }, userEmail: email };
    },
    staleTime: Infinity,
    retry: false,
  });

  const user = data?.user || {};
  const userEmail = data?.userEmail;

  const Update = (updates) => {
    queryClient.setQueryData([...USER_QUERY_KEY, encryptedData], (old) => ({
      ...old,
      user: { ...old?.user, ...updates },
    }));
  };

  const clear = () => {
    queryClient.setQueryData([...USER_QUERY_KEY, encryptedData], { user: {}, userEmail: undefined });
  };

  const value = useMemo(() => [user, Update, clear, userEmail], [user, userEmail]);

  return isLoading ? (
    <Loader />
  ) : (
    <UserContext.Provider value={value}>{children}</UserContext.Provider>
  );
}

// Material Dashboard 2 React custom hook for using context
function useUserInfo() {
  return useContext(UserContext) || [];
}

// Typechecking props for the MaterialUIControllerProvider
UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { UserProvider, useUserInfo };
